import { useState, useEffect, useRef } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trackEvent } from "@/lib/analytics";
import {
  useGenerateJobImage,
  useRenderJobVideo,
  useResetCinematicImages,
  useRegenerateJob,
  useUpdateUserPreferences,
  useGetCurrentUser,
  getGetJobQueryKey,
  type Job,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Video,
  Wand2,
  Download,
  Loader2,
  RefreshCw,
  Crown,
  AlertCircle,
  ZoomIn,
  X,
  Film,
  ImageIcon,
  Layers,
  RotateCcw,
} from "lucide-react";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const IMAGE_GENERATION_MONTHLY_LIMIT = 50;
const IMAGE_GENERATION_PER_JOB_LIMIT = 2;
const CINEMATIC_VIDEO_MONTHLY_LIMIT = 10;

type VideoFormat = "portrait" | "landscape" | "square";
type ImageSource = "artwork" | "ai" | "cinematic";
type ImageStyle = "grounded" | "cinematic" | "atmospheric";

const FORMAT_OPTIONS: {
  value: VideoFormat;
  label: string;
  ratio: string;
  widthClass: string;
  heightClass: string;
  thumbClass: string;
}[] = [
  { value: "portrait", label: "Portrait", ratio: "9:16", widthClass: "w-6", heightClass: "h-10", thumbClass: "w-[90px] h-40" },
  { value: "landscape", label: "Landscape", ratio: "16:9", widthClass: "w-10", heightClass: "h-6", thumbClass: "w-40 h-[90px]" },
  { value: "square", label: "Square", ratio: "1:1", widthClass: "w-8", heightClass: "h-8", thumbClass: "w-[120px] h-[120px]" },
];

const FORMAT_STORAGE_KEY = "podcasts-abbreviated:video-format";
const STYLE_STORAGE_KEY = "podcasts-abbreviated:image-style";
const IMAGE_SOURCE_STORAGE_KEY = "podcasts-abbreviated:image-source";

const STYLE_OPTIONS: {
  value: ImageStyle;
  label: string;
  tagline: string;
  bestFor: string;
}[] = [
  {
    value: "grounded",
    label: "Grounded",
    tagline: "Real-world, photographic. Builds trust.",
    bestFor: "Business, Science, News, How-to, Expert Interview",
  },
  {
    value: "cinematic",
    label: "Cinematic",
    tagline: "Epic scale, dramatic light. Commands attention.",
    bestFor: "Tech, Motivation, Future, Big Ideas",
  },
  {
    value: "atmospheric",
    label: "Atmospheric",
    tagline: "Moody, high-contrast, textured. Creates feeling.",
    bestFor: "True Crime, Philosophy, Fiction, Personal Narrative",
  },
];

function toApiStyle(style: ImageStyle): "Grounded" | "Cinematic" | "Atmospheric" {
  return (style.charAt(0).toUpperCase() + style.slice(1)) as "Grounded" | "Cinematic" | "Atmospheric";
}

interface PlanInfo {
  plan: "free" | "pro";
  imageGenerationsUsed?: number;
  cinematicVideosUsed?: number;
}

interface Props {
  job: Job;
  jobId: number;
}

const RENDER_MULTIPLIER = 1.5;
const CINEMATIC_RENDER_MULTIPLIER = 5;
const MIN_ESTIMATED_SECONDS = 30;

const MAX_ERROR_DISPLAY_LENGTH = 200;

export function formatVideoError(raw: string): string {
  if (/UNAVAILABLE|high demand|quota|503/i.test(raw)) {
    return "Google's AI is currently busy — please try again in a minute.";
  }
  const firstLine = raw.split("\n").find((l) => l.trim().length > 0) ?? raw;
  const trimmed = firstLine.trim();
  const truncated =
    trimmed.length > MAX_ERROR_DISPLAY_LENGTH
      ? trimmed.slice(0, MAX_ERROR_DISPLAY_LENGTH).trimEnd() + "…"
      : trimmed;
  if (/^video render failed/i.test(truncated)) {
    return truncated;
  }
  if (/beat \d+ is too short to crossfade/i.test(truncated)) {
    return truncated;
  }
  return `Encoding failed: ${truncated}`;
}

function formatSeconds(s: number): string {
  const total = Math.floor(s);
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function useRenderProgress(active: boolean, estimatedTotal: number) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number | null>(null);

  useEffect(() => {
    if (!active) {
      startRef.current = null;
      setElapsed(0);
      return;
    }
    if (startRef.current === null) {
      startRef.current = Date.now();
    }
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current!) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  const capped = Math.max(estimatedTotal, MIN_ESTIMATED_SECONDS);
  const progress = Math.min(elapsed / capped, 0.95);
  const overEstimate = elapsed > capped;
  const remaining = Math.max(capped - elapsed, 0);

  return { progress, overEstimate, timeRemaining: formatSeconds(remaining) };
}

function readStoredImageSource(job: Job): ImageSource | null {
  if (typeof window === "undefined") return job.artworkUrl ? "artwork" : null;
  const stored = localStorage.getItem(IMAGE_SOURCE_STORAGE_KEY);
  if (stored === "artwork" || stored === "ai" || stored === "cinematic") return stored;
  return job.artworkUrl ? "artwork" : null;
}

export function CreateVideoSection({ job, jobId }: Props) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const updatePreferencesMutation = useUpdateUserPreferences();
  const { data: currentUser } = useGetCurrentUser();

  const { data: planData } = useQuery<PlanInfo>({
    queryKey: ["plan"],
    enabled: false,
    staleTime: Infinity,
  });

  const isPro = planData?.plan === "pro";
  const imageGenerationsUsed = planData?.imageGenerationsUsed ?? 0;
  const cinematicVideosUsed = planData?.cinematicVideosUsed ?? 0;

  const [imageSource, setImageSource] = useState<ImageSource | null>(() => readStoredImageSource(job));

  const handleImageSourceChange = (source: ImageSource) => {
    setImageSource(source);
    if (typeof window !== "undefined") {
      localStorage.setItem(IMAGE_SOURCE_STORAGE_KEY, source);
    }
  };

  const [videoFormat, setVideoFormat] = useState<VideoFormat>(() => {
    if (job.videoFormat === "portrait" || job.videoFormat === "landscape" || job.videoFormat === "square") {
      return job.videoFormat;
    }
    if (typeof window === "undefined") return "portrait";
    const stored = localStorage.getItem(FORMAT_STORAGE_KEY);
    if (stored === "portrait" || stored === "landscape" || stored === "square") return stored;
    return "portrait";
  });

  // Sync format from DB user preferences when they load, but only if the user
  // hasn't already made a local choice (localStorage key is unset and job has no format).
  useEffect(() => {
    if (!currentUser?.preferredVideoFormat) return;
    const pref = currentUser.preferredVideoFormat;
    if (pref !== "portrait" && pref !== "landscape" && pref !== "square") return;
    // Only apply DB default if job has no saved format and localStorage has no value
    if (job.videoFormat) return;
    const stored = typeof window !== "undefined" ? localStorage.getItem(FORMAT_STORAGE_KEY) : null;
    if (!stored) {
      setVideoFormat(pref);
    }
  }, [currentUser?.preferredVideoFormat]);

  const formatDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFormatChange = (format: VideoFormat) => {
    setVideoFormat(format);
    if (typeof window !== "undefined") {
      localStorage.setItem(FORMAT_STORAGE_KEY, format);
    }
    // Debounce-sync to server so user account defaults stay up to date
    if (formatDebounceRef.current) clearTimeout(formatDebounceRef.current);
    formatDebounceRef.current = setTimeout(() => {
      updatePreferencesMutation.mutate({ data: { preferredVideoFormat: format } });
    }, 800);
  };

  const [selectedStyle, setSelectedStyle] = useState<ImageStyle>(() => {
    if (typeof window === "undefined") return "cinematic";
    const stored = localStorage.getItem(STYLE_STORAGE_KEY);
    return (STYLE_OPTIONS.find((s) => s.value === stored)?.value) ?? "cinematic";
  });

  const handleStyleChange = (style: ImageStyle) => {
    setSelectedStyle(style);
    if (typeof window !== "undefined") {
      localStorage.setItem(STYLE_STORAGE_KEY, style);
    }
  };

  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);
  const [audioExpired, setAudioExpired] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!previewImageUrl) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewImageUrl(null);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [previewImageUrl]);

  const hasArtwork = !!job.artworkUrl;
  const hasAiImage = !!job.generatedImageUrl;
  const hasVideo = !!job.videoUrl;
  const aiImageMissing = imageSource === "ai" && !hasAiImage;
  const hasCinematicBeats = Array.isArray(job.cinematicImages) && (job.cinematicImages as unknown[]).length > 0;
  const cinematicBeatCount = hasCinematicBeats ? (job.cinematicImages as unknown[]).length : 0;
  const cinematicStyleMismatch = hasCinematicBeats &&
    (job.cinematicStyle?.toLowerCase() !== selectedStyle || job.cinematicFormat !== videoFormat);

  const isRenderingFromJob = job.videoStatus === "rendering";
  const isRenderFailed = job.videoStatus === "failed";

  const [localRendering, setLocalRendering] = useState(false);

  const estimatedTotalRef = useRef<number>(MIN_ESTIMATED_SECONDS);

  const serverProgress = job.videoRenderProgress;
  const hasLiveServerProgress = serverProgress !== null && serverProgress !== undefined;

  // A render is stale when the job is in "rendering" state but the user didn't
  // just start it (localRendering=false) AND either:
  //   a) no progress value is present at all, OR
  //   b) updatedAt is >10 min old — meaning the worker wrote a progress value
  //      (e.g. 92%) and then crashed before completing, leaving stale DB state.
  // 20 min: max gap between server-side updatedAt refreshes is one xfade
  // group pass duration (~10 min ceiling). 2× headroom prevents false
  // positives on healthy long renders while still catching real crashes.
  const STALE_RENDER_MS = 20 * 60 * 1000;
  const jobUpdatedAt = job.updatedAt ? new Date(job.updatedAt).getTime() : null;
  const isUpdatedAtStale = jobUpdatedAt !== null && Date.now() - jobUpdatedAt > STALE_RENDER_MS;
  const isStaleRender = isRenderingFromJob && !localRendering && (!hasLiveServerProgress || isUpdatedAtStale);
  const isRendering = localRendering || (isRenderingFromJob && hasLiveServerProgress && !isUpdatedAtStale);

  const { progress: estimatedProgress, overEstimate, timeRemaining } = useRenderProgress(
    isRendering,
    estimatedTotalRef.current,
  );

  const hasServerProgress = isRendering && hasLiveServerProgress;
  const progress = hasServerProgress ? serverProgress / 100 : estimatedProgress;

  useEffect(() => {
    if (!isRenderingFromJob || isStaleRender) return;
    const id = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: getGetJobQueryKey(jobId) });
    }, 3000);
    return () => clearInterval(id);
  }, [isRenderingFromJob, isStaleRender, jobId, queryClient]);

  const prevHasVideoRef = useRef(hasVideo);
  useEffect(() => {
    if (hasVideo || isRenderFailed || isStaleRender) {
      setLocalRendering(false);
    }
    if (hasVideo && !prevHasVideoRef.current) {
      trackEvent("social_video_created", { job_id: jobId, format: videoFormat });
    }
    prevHasVideoRef.current = hasVideo;
  }, [hasVideo, isRenderFailed, isStaleRender, jobId, videoFormat]);

  const perJobCount = job.imageGenerationsCount ?? 0;
  const canGenerate =
    isPro &&
    perJobCount < IMAGE_GENERATION_PER_JOB_LIMIT &&
    imageGenerationsUsed < IMAGE_GENERATION_MONTHLY_LIMIT;

  const { mutate: generateImage, isPending: isGenerating } =
    useGenerateJobImage({
      mutation: {
        onSuccess: (updatedJob) => {
          queryClient.setQueryData(getGetJobQueryKey(jobId), updatedJob);
          void queryClient.invalidateQueries({ queryKey: ["plan"] });
          handleImageSourceChange("ai");
        },
        onError: (err: unknown) => {
          const status = (err as { status?: number })?.status;
          if (status === 429) {
            toast({
              title: "Quota reached",
              description: "Monthly AI image limit reached. Resets at the start of your next billing cycle.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Generation failed",
              description: "Could not generate AI image. Please try again.",
              variant: "destructive",
            });
          }
        },
      },
    });

  const { mutate: renderVideo } = useRenderJobVideo({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetJobQueryKey(jobId) });
        setAudioExpired(false);
      },
      onError: (err: unknown) => {
        setLocalRendering(false);
        const status = (err as { status?: number })?.status;
        if (status === 410) {
          setAudioExpired(true);
        } else {
          toast({
            title: "Render failed",
            description: "Could not start video render. Please try again.",
            variant: "destructive",
          });
        }
      },
    },
  });

  const { mutate: resetCinematicImages, isPending: isResetting } = useResetCinematicImages({
    mutation: {
      onSuccess: (updatedJob) => {
        queryClient.setQueryData(getGetJobQueryKey(jobId), updatedJob);
        toast({ title: "Cinematic images cleared", description: "Next render will regenerate from scratch." });
      },
      onError: () => {
        toast({ title: "Reset failed", description: "Could not clear cinematic images.", variant: "destructive" });
      },
    },
  });

  const { mutate: regenerateForVideo, isPending: isRegeneratingForVideo } =
    useRegenerateJob({
      mutation: {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getGetJobQueryKey(jobId) });
          setAudioExpired(false);
        },
        onError: (err: unknown) => {
          const msg =
            (err as { message?: string })?.message ??
            "Could not regenerate this job. Please try again.";
          toast({ title: "Regenerate failed", description: msg, variant: "destructive" });
        },
      },
    });

  const handleCreateVideo = (options?: { force?: boolean }) => {
    if (!imageSource) return;
    const outputSecs = job.outputDurationSeconds ?? 0;
    estimatedTotalRef.current = imageSource === "cinematic"
      ? Math.max(outputSecs * CINEMATIC_RENDER_MULTIPLIER, 120)
      : Math.max(outputSecs * RENDER_MULTIPLIER, MIN_ESTIMATED_SECONDS);
    setLocalRendering(true);
    renderVideo({
      id: jobId,
      data: {
        imageSource,
        format: videoFormat,
        cinematicStyle: toApiStyle(selectedStyle),
        ...(options?.force ? { force: true } : {}),
      },
    });
  };

  const handleRegenerateForVideo = () => {
    regenerateForVideo({
      id: jobId,
      data: {
        mode: job.mode as "key_quotes" | "quick_catchup" | "full_recap",
        targetPercent: job.targetPercent ?? 40,
      },
    });
  };

  const handleDownloadVideo = async () => {
    if (isDownloadingVideo) return;
    setIsDownloadingVideo(true);
    try {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/jobs/${jobId}/video-url`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const { url } = await res.json() as { url: string };
      const a = document.createElement("a");
      a.href = url;
      a.download = `podcast_clip_${jobId}.mp4`;
      a.click();
    } catch {
      toast({
        title: "Download failed",
        description: "Could not download the video. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingVideo(false);
    }
  };

  return (
    <Card className="bg-card/50 border-border/50" data-testid="card-create-video">
      <CardHeader className="pb-4 border-b border-border/10">
        <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
          <Video size={16} className="text-primary" />
          Create Video Clip
        </CardTitle>
      </CardHeader>

      <CardContent className="pt-5 space-y-5">

        {/* ── IMAGE SOURCE: three-card selector ── */}
        <div className="space-y-3">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
            Background
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="image-source-picker">

            {/* Card 1 — Artwork */}
            <button
              type="button"
              onClick={() => hasArtwork && handleImageSourceChange("artwork")}
              data-testid="button-select-artwork"
              aria-pressed={imageSource === "artwork"}
              disabled={!hasArtwork}
              title={hasArtwork ? "Use podcast artwork" : "No artwork available for this episode"}
              className={[
                "relative flex flex-col rounded-lg border-2 overflow-hidden transition-all duration-200 focus:outline-none text-left",
                !hasArtwork
                  ? "opacity-40 cursor-not-allowed border-border/30"
                  : imageSource === "artwork"
                    ? "border-primary ring-2 ring-primary/30"
                    : "border-border/40 hover:border-primary/40",
              ].join(" ")}
            >
              {hasArtwork ? (
                <div className="relative w-full aspect-square">
                  <img
                    src={job.artworkUrl!}
                    alt="Podcast artwork"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                  {imageSource === "artwork" && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow">
                        <span className="text-primary-foreground text-xs font-bold">✓</span>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPreviewImageUrl(job.artworkUrl!); }}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                    data-testid="button-preview-artwork"
                    title="Preview full image"
                  >
                    <ZoomIn size={12} className="text-white" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center aspect-square bg-muted/30">
                  <ImageIcon size={24} className="text-muted-foreground/40" />
                </div>
              )}
              <div className="px-2.5 py-2 bg-background/60">
                <span className="text-xs font-mono font-semibold text-foreground/80">Artwork</span>
                <p className="text-[10px] font-mono text-muted-foreground/70 leading-tight mt-0.5">
                  {hasArtwork ? "Episode cover art" : "Not available"}
                </p>
              </div>
            </button>

            {/* Card 2 — AI Image */}
            <button
              type="button"
              onClick={() => isPro && handleImageSourceChange("ai")}
              data-testid="button-select-ai-image"
              aria-pressed={imageSource === "ai"}
              disabled={!isPro}
              title={isPro ? "Use AI-generated image" : "Pro plan required"}
              className={[
                "relative flex flex-col rounded-lg border-2 overflow-hidden transition-all duration-200 focus:outline-none text-left",
                !isPro
                  ? "opacity-60 cursor-not-allowed border-border/30"
                  : imageSource === "ai"
                    ? "border-primary ring-2 ring-primary/30"
                    : "border-border/40 hover:border-primary/40",
              ].join(" ")}
            >
              {hasAiImage ? (
                <div className="relative w-full aspect-square">
                  <img
                    src={`${basePath}/api/jobs/${jobId}/generated-image`}
                    alt="AI generated image"
                    className="w-full h-full object-cover"
                  />
                  {imageSource === "ai" && (
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow">
                        <span className="text-primary-foreground text-xs font-bold">✓</span>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPreviewImageUrl(`${basePath}/api/jobs/${jobId}/generated-image`); }}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                    data-testid="button-preview-ai-image"
                    title="Preview full image"
                  >
                    <ZoomIn size={12} className="text-white" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center aspect-square bg-muted/20">
                  <Wand2 size={24} className="text-muted-foreground/40" />
                </div>
              )}
              <div className="px-2.5 py-2 bg-background/60 flex items-start justify-between gap-1">
                <div>
                  <span className="text-xs font-mono font-semibold text-foreground/80">AI Image</span>
                  <p className="text-[10px] font-mono text-muted-foreground/70 leading-tight mt-0.5">
                    {hasAiImage ? "Single AI image" : "Not generated yet"}
                  </p>
                </div>
                {!isPro && (
                  <Badge variant="outline" className="text-[9px] font-mono text-amber-600 border-amber-500/40 bg-amber-500/10 gap-0.5 flex-shrink-0 px-1 py-0">
                    <Crown size={8} />Pro
                  </Badge>
                )}
              </div>
            </button>

            {/* Card 3 — Cinematic Slideshow */}
            <button
              type="button"
              onClick={() => isPro && handleImageSourceChange("cinematic")}
              data-testid="button-select-cinematic"
              aria-pressed={imageSource === "cinematic"}
              disabled={!isPro}
              title={isPro ? "Use cinematic AI slideshow" : "Pro plan required"}
              className={[
                "relative flex flex-col rounded-lg border-2 overflow-hidden transition-all duration-200 focus:outline-none text-left",
                !isPro
                  ? "opacity-60 cursor-not-allowed border-border/30"
                  : imageSource === "cinematic"
                    ? "border-primary ring-2 ring-primary/30"
                    : "border-border/40 hover:border-primary/40",
              ].join(" ")}
            >
              <div className="flex items-center justify-center aspect-square bg-gradient-to-br from-indigo-950/60 to-purple-950/60 relative overflow-hidden">
                <div className="absolute inset-0 flex gap-0.5 opacity-20">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex-1 bg-indigo-400/50 rounded-sm" style={{ height: `${60 + (i % 2) * 20}%`, alignSelf: "flex-end", marginBottom: `${(i % 3) * 8}%` }} />
                  ))}
                </div>
                <div className="relative flex flex-col items-center gap-1.5">
                  <Film size={28} className="text-indigo-300/80" />
                  {hasCinematicBeats && (
                    <div className="flex items-center gap-1 bg-indigo-900/70 rounded px-1.5 py-0.5">
                      <Layers size={8} className="text-indigo-300" />
                      <span className="text-[9px] font-mono text-indigo-200">{cinematicBeatCount} scenes</span>
                    </div>
                  )}
                  {imageSource === "cinematic" && (
                    <div className="absolute -top-8 -right-8 w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow">
                      <span className="text-primary-foreground text-xs font-bold">✓</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="px-2.5 py-2 bg-background/60 flex items-start justify-between gap-1">
                <div>
                  <span className="text-xs font-mono font-semibold text-foreground/80">Cinematic</span>
                  <p className="text-[10px] font-mono text-muted-foreground/70 leading-tight mt-0.5">
                    {hasCinematicBeats ? `${cinematicBeatCount} AI scenes` : "AI slideshow per beat"}
                  </p>
                </div>
                {!isPro && (
                  <Badge variant="outline" className="text-[9px] font-mono text-amber-600 border-amber-500/40 bg-amber-500/10 gap-0.5 flex-shrink-0 px-1 py-0">
                    <Crown size={8} />Pro
                  </Badge>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* ── AI IMAGE: generate / regenerate controls (shown when AI Image selected) ── */}
        {imageSource === "ai" && isPro && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => generateImage({ id: jobId, data: { format: videoFormat, style: selectedStyle } })}
              disabled={!canGenerate || isGenerating}
              className="font-mono text-xs gap-1.5"
              data-testid="button-generate-image"
            >
              {isGenerating ? (
                <Loader2 size={12} className="animate-spin" />
              ) : hasAiImage ? (
                <RefreshCw size={12} />
              ) : (
                <Wand2 size={12} />
              )}
              {isGenerating
                ? "Generating…"
                : hasAiImage
                  ? "Regenerate Cover Image"
                  : "Generate Cover Image"}
            </Button>
            <span className="text-xs font-mono text-muted-foreground" data-testid="text-image-generations-used">
              {imageGenerationsUsed}/{IMAGE_GENERATION_MONTHLY_LIMIT} monthly
            </span>
          </div>
        )}

        {/* ── CINEMATIC: pre-gen status + reset ── */}
        {imageSource === "cinematic" && isPro && hasCinematicBeats && (
          <div className={[
            "flex items-start gap-3 rounded-lg border px-3 py-2.5",
            cinematicStyleMismatch
              ? "border-amber-500/30 bg-amber-500/10"
              : "border-indigo-500/20 bg-indigo-500/10",
          ].join(" ")}>
            {cinematicStyleMismatch ? (
              <AlertCircle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
            ) : (
              <Layers size={14} className="text-indigo-400 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className={[
                "text-xs font-mono leading-snug",
                cinematicStyleMismatch ? "text-amber-200" : "text-indigo-200",
              ].join(" ")}>
                {cinematicStyleMismatch
                  ? `${cinematicBeatCount} scenes ready — style or format changed, images will regenerate.`
                  : `${cinematicBeatCount} scenes pre-generated and ready.`}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => resetCinematicImages({ id: jobId })}
              disabled={isResetting}
              className="text-xs font-mono gap-1 text-muted-foreground hover:text-foreground flex-shrink-0 h-6 px-2"
              data-testid="button-reset-cinematic"
              title="Clear pre-generated scenes to start fresh"
            >
              {isResetting ? <Loader2 size={10} className="animate-spin" /> : <RotateCcw size={10} />}
              Reset
            </Button>
          </div>
        )}
        {imageSource === "cinematic" && isPro && !hasCinematicBeats && (
          <p className="text-xs font-mono text-muted-foreground/70">
            AI scenes will be generated when you create the video (takes ~2–4 min).
          </p>
        )}
        {imageSource === "cinematic" && isPro && (
          <div className="flex items-center gap-2">
            <span
              className={[
                "text-xs font-mono",
                cinematicVideosUsed >= CINEMATIC_VIDEO_MONTHLY_LIMIT
                  ? "text-red-400"
                  : "text-muted-foreground",
              ].join(" ")}
              data-testid="text-cinematic-videos-used"
            >
              {cinematicVideosUsed}/{CINEMATIC_VIDEO_MONTHLY_LIMIT} cinematic videos this month
            </span>
          </div>
        )}

        {/* ── VIBE / STYLE picker (shown for all image sources — affects Ken Burns intensity for artwork/AI, cinematic scene style for cinematic) ── */}
        {imageSource && isPro && (
          <div className="space-y-3">
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
              {imageSource === "cinematic" ? "Cinematic Style" : imageSource === "artwork" ? "Motion Style" : "Image Style"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="style-picker">
              {STYLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleStyleChange(opt.value)}
                  data-testid={`button-style-${opt.value}`}
                  aria-pressed={selectedStyle === opt.value}
                  className={[
                    "relative flex flex-col gap-1.5 rounded-lg border-2 px-3 py-3 text-left transition-all duration-200 focus:outline-none",
                    selectedStyle === opt.value
                      ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                      : "border-border/40 hover:border-primary/40",
                  ].join(" ")}
                >
                  {selectedStyle === opt.value && (
                    <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-primary-foreground text-[9px] font-bold leading-none">✓</span>
                    </div>
                  )}
                  <div className={[
                    "text-xs font-mono font-semibold leading-tight",
                    selectedStyle === opt.value ? "text-foreground" : "text-muted-foreground",
                  ].join(" ")}>
                    {opt.label}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground/80 leading-tight">
                    {opt.tagline}
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground/50 leading-tight">
                    {opt.bestFor}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── NO IMAGE AVAILABLE message ── */}
        {!hasArtwork && !hasAiImage && imageSource !== "cinematic" && !isPro && (
          <p className="text-xs font-mono text-muted-foreground max-w-64">
            No podcast artwork found. Upgrade to Pro to generate an AI image or use the Cinematic Slideshow.
          </p>
        )}

        {/* ── VIDEO FORMAT picker ── */}
        <div className="space-y-3">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
            Video Format
          </p>
          <div className="flex gap-3 flex-wrap" data-testid="format-picker">
            {FORMAT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleFormatChange(opt.value)}
                data-testid={`button-format-${opt.value}`}
                className={[
                  "flex flex-col items-center gap-2 rounded-lg border-2 px-4 py-3 transition-all duration-200 focus:outline-none",
                  videoFormat === opt.value
                    ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                    : "border-border/40 hover:border-primary/40",
                ].join(" ")}
              >
                <div className="flex items-center justify-center w-12 h-12">
                  <div
                    className={[
                      "rounded-sm border-2",
                      videoFormat === opt.value ? "border-primary bg-primary/20" : "border-muted-foreground/50 bg-muted/30",
                      opt.widthClass,
                      opt.heightClass,
                    ].join(" ")}
                  />
                </div>
                <div className="text-center">
                  <div className="text-xs font-mono font-medium leading-tight">
                    {opt.label}
                  </div>
                  <div className="text-xs font-mono text-muted-foreground leading-tight">
                    {opt.ratio}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── AUDIO EXPIRED warning ── */}
        {audioExpired && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <AlertCircle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-mono text-amber-200 leading-snug">
                Processed audio has expired. Regenerate this job to restore it, then create the video.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleRegenerateForVideo}
                disabled={isRegeneratingForVideo}
                className="mt-2 font-mono text-xs gap-1.5 border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                data-testid="button-regenerate-for-video"
              >
                {isRegeneratingForVideo ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}
                {isRegeneratingForVideo ? "Regenerating…" : "Regenerate Job"}
              </Button>
            </div>
          </div>
        )}

        {/* ── STALE RENDER notice ── */}
        {isStaleRender && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <AlertCircle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-mono text-amber-200 leading-snug">
                A render may have been interrupted by a server restart.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCreateVideo({ force: true })}
                disabled={!imageSource}
                className="mt-2 font-mono text-xs gap-1.5 border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                data-testid="button-retry-stale-render"
              >
                <RefreshCw size={12} />
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* ── RENDER FAILED notice ── */}
        {isRenderFailed && !isRendering && (
          <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3" data-testid="render-failed-notice">
            <AlertCircle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-mono text-red-300 leading-snug" data-testid="render-failed-message">
                {job.videoErrorMessage
                  ? formatVideoError(job.videoErrorMessage)
                  : "Render failed."}
              </p>
              <p className="mt-1 text-xs font-mono text-red-400/70 leading-snug">
                Select a background and try again.
              </p>
            </div>
          </div>
        )}

        {/* ── FORMAT MISMATCH notice ── */}
        {hasVideo && job.videoFormat && job.videoFormat !== videoFormat && !isRendering && (
          <div className="flex items-start gap-3 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3" data-testid="format-mismatch-notice">
            <AlertCircle size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-mono text-blue-300 leading-snug">
              Current video is{" "}
              <span className="font-semibold">
                {FORMAT_OPTIONS.find((o) => o.value === job.videoFormat)?.label}{" "}
                {FORMAT_OPTIONS.find((o) => o.value === job.videoFormat)?.ratio}
              </span>
              . Re-render to get{" "}
              <span className="font-semibold">
                {FORMAT_OPTIONS.find((o) => o.value === videoFormat)?.label}{" "}
                {FORMAT_OPTIONS.find((o) => o.value === videoFormat)?.ratio}
              </span>
              .
            </p>
          </div>
        )}

        {/* ── AI IMAGE MISSING hint ── */}
        {aiImageMissing && isPro && !isRendering && (
          <div className="flex items-start gap-3 rounded-lg border border-muted bg-muted/30 px-4 py-3" data-testid="ai-image-missing-notice">
            <AlertCircle size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-sm font-mono text-muted-foreground leading-snug">
              Generate a cover image above before creating the video.
            </p>
          </div>
        )}

        {/* ── ACTION ROW ── */}
        <div className="flex items-center gap-3 pt-1 flex-wrap">
          {hasVideo && !isRendering ? (
            <Button
              onClick={handleDownloadVideo}
              disabled={isDownloadingVideo}
              className="font-mono uppercase text-xs tracking-wider gap-2"
              data-testid="button-download-video"
            >
              {isDownloadingVideo ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {isDownloadingVideo
                ? "Downloading…"
                : job.videoFormat
                  ? `Download Video · ${FORMAT_OPTIONS.find((o) => o.value === job.videoFormat)?.label} ${FORMAT_OPTIONS.find((o) => o.value === job.videoFormat)?.ratio}`
                  : "Download Video"}
            </Button>
          ) : isRendering ? (
            <div className="flex flex-col gap-1.5 w-full max-w-xs" data-testid="render-progress-container">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono text-muted-foreground flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin text-primary" />
                  {imageSource === "cinematic"
                    ? (() => {
                        const phase = job.cinematicRenderPhase;
                        if (phase?.phase === "generating_beats") return "Analyzing episode structure…";
                        if (phase?.phase === "generating_images" && phase.total) {
                          return `Generating images ${phase.completed ?? 0} of ${phase.total}…`;
                        }
                        return "Generating scenes & rendering…";
                      })()
                    : "Rendering…"}
                </span>
                <span className="text-xs font-mono text-muted-foreground" data-testid="render-progress-eta">
                  {hasServerProgress
                    ? `${serverProgress}%`
                    : overEstimate
                      ? "Taking longer than expected…"
                      : `Est. ${timeRemaining} remaining`}
                </span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-1000 ease-linear"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                  data-testid="render-progress-bar"
                />
              </div>
            </div>
          ) : (
            <Button
              onClick={() => handleCreateVideo()}
              disabled={!imageSource || isRendering || aiImageMissing || (imageSource === "cinematic" && cinematicVideosUsed >= CINEMATIC_VIDEO_MONTHLY_LIMIT)}
              className="font-mono uppercase text-xs tracking-wider gap-2"
              data-testid="button-create-video"
              title={imageSource === "cinematic" && cinematicVideosUsed >= CINEMATIC_VIDEO_MONTHLY_LIMIT
                ? `Monthly cinematic video limit of ${CINEMATIC_VIDEO_MONTHLY_LIMIT} reached. Resets at the start of your next billing cycle.`
                : undefined}
            >
              <Video size={14} />
              Create Video
            </Button>
          )}

          {hasVideo && !isRendering && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCreateVideo()}
              disabled={!imageSource || isRendering || aiImageMissing || (imageSource === "cinematic" && cinematicVideosUsed >= CINEMATIC_VIDEO_MONTHLY_LIMIT)}
              className="font-mono text-xs gap-1.5 text-muted-foreground"
              data-testid="button-re-render-video"
              title={imageSource === "cinematic" && cinematicVideosUsed >= CINEMATIC_VIDEO_MONTHLY_LIMIT
                ? `Monthly cinematic video limit of ${CINEMATIC_VIDEO_MONTHLY_LIMIT} reached. Resets at the start of your next billing cycle.`
                : undefined}
            >
              <RefreshCw size={12} />
              Re-render ·{" "}
              {FORMAT_OPTIONS.find((o) => o.value === videoFormat)?.label}{" "}
              {FORMAT_OPTIONS.find((o) => o.value === videoFormat)?.ratio}
            </Button>
          )}
        </div>
      </CardContent>

      {/* ── IMAGE PREVIEW LIGHTBOX ── */}
      {previewImageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setPreviewImageUrl(null)}
          data-testid="image-preview-overlay"
        >
          <div
            className="relative max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewImageUrl(null)}
              className="absolute -top-3 -right-3 z-10 w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center shadow-lg hover:bg-muted transition-colors"
              data-testid="button-close-image-preview"
              title="Close preview"
            >
              <X size={14} />
            </button>
            <img
              src={previewImageUrl}
              alt="Full-size preview"
              className="w-full h-auto rounded-lg shadow-2xl"
              data-testid="image-preview-full"
            />
          </div>
        </div>
      )}
    </Card>
  );
}
